/**
 * 用户信息类型定义
 */
export interface UserInfo {
  /**
   * 用户头像URL
   * @remarks 可能为null表示未设置头像
   */
  avatar: string | null;

  /**
   * 删除标志
   * @remarks "0"表示未删除，"1"表示已删除
   * @default "0"
   */
  delFlag: string;

  /**
   * 电子邮箱
   * @remarks 可能为null表示未绑定邮箱
   */
  email: string | null;

  /**
   * 身份证号码
   * @example "610121199102021122"
   */
  idNumber: string;

  /**
   * 用户昵称
   * @example "张三"
   */
  nickName: string;

  /**
   * 第三方开放ID
   * @example "otMrs6BYiCz4nngo1rU5vVwVi7OA"
   */
  openId: string;

  /**
   * 加密后的密码
   * @remarks 使用BCrypt算法加密
   * @example "$2a$10$tQbr6F38HTOtuygrh/JPQu7paGfjVSi.IYpAwDjDEArF2oxlP2lP2"
   */
  password: string;

  /**
   * 手机号码
   * @example "18729026711"
   */
  phonenumber: string;

  /**
   * 系统唯一序列号
   * @example "1945401433551618050"
   */
  sequenceNbr: string;

  /**
   * 账号状态
   * @remarks "0"表示正常，"1"表示禁用
   * @default "0"
   */
  status: string;

  /**
   * 用户类型
   * @example "业主"
   */
  userType: string;
}

/**
 * 房产/房间信息类型定义
 */
export interface PropertyInfo {
  /**
   * 申请类型
   * @remarks 可能为null表示未设置申请类型
   */
  applyType: string | null;

  /**
   * 审核状态
   * @remarks "0"=未审核，"1"=已通过，"2"=已拒绝（根据业务常见状态设计）
   */
  auditStatus: string;

  /**
   * 审核时间
   * @remarks 可能为null表示尚未审核
   */
  auditTime: string | null;

  /**
   * 审核人
   * @remarks 可能为null表示尚未审核或系统自动审核
   */
  auditor: string | null;

  /**
   * 绑定方式
   * @remarks 可能为null表示未指定绑定方式
   */
  bindMethod: string | null;

  /**
   * 社区/小区ID
   * @example "1933063875717734402"
   */
  communityId: string;

  /**
   * 社区/小区名称
   * @example "万科超高端运镜"
   */
  communityName: string;

  /**
   * 创建人
   * @remarks 可能为null表示系统创建
   */
  createBy: string | null;

  /**
   * 创建时间
   * @remarks 可能为null表示尚未记录创建时间
   */
  createTime: string | null;

  /**
   * 客户名称
   * @example "彩色里"
   */
  customerName: string;

  /**
   * 客户电话
   * @example "18789898989"
   */
  customerPhone: string;

  /**
   * 楼栋ID
   * @example "1934518081284710401"
   */
  floorId: string;

  /**
   * 楼栋名称
   * @example "楼栋1002"
   */
  floorName: string;

  /**
   * 昵称
   * @remarks 可能为null表示未设置昵称
   */
  nickName: string | null;

  /**
   * 业主ID
   * @remarks 可能为null表示尚未关联业主
   */
  ownerId: string | null;

  /**
   * 业主电话
   * @remarks 可能为null表示尚未关联业主
   */
  ownerPhone: string | null;

  /**
   * 手机号码
   * @remarks 可能为null表示未绑定手机
   */
  phonenumber: string | null;

  /**
   * 区域ID
   * @example "2000001"
   */
  regionId: string;

  /**
   * 区域名称
   * @example "片区00011"
   */
  regionName: string;

  /**
   * 房间编号
   * @example "1-2021"
   */
  roomCode: string;

  /**
   * 房间ID
   * @example "1935938669219876866"
   */
  roomId: string;

  /**
   * 房间名称
   * @example "1-2021"
   */
  roomName: string;

  /**
   * 系统唯一序列号
   * @example "1954733833326788610"
   */
  sequenceNbr: string;

  /**
   * 单元ID
   * @example "1934518097185316865"
   */
  unitId: string;

  /**
   * 单元名称
   * @example "1"
   */
  unitName: string;

  /**
   * 用户类型
   * @remarks 可能为null表示未指定用户类型
   */
  userType: string | null;

  defaultFlag: string;
}

export interface OwnerAssetsCountType {
  /**
   * 车辆数量
   */
  car: string;
  /**
   * 车位数量
   */
  park: string;
  /**
   * 房间数量
   */
  room: string;
}

export interface OwnerAssetsType {
  car: CarType[];
  park: ParkType[];
}

export interface CarType {
  carCode: string;
  parkCode: string;
  sequenceNbr: string;
  color: string;
}

export interface ParkType {
  carNum: string;
  parkCode: string;
  sequenceNbr: string;
  parkType: string;
}

/**
 * 车位类型
 */
export const parkTypeMap: Record<string, string> = {
  packYear: '包年',
  packMonth: '包月',
  buyUP: '买断车位',
  motherChild: '子母车位',
  ordinary: '普通车位',
  hire: '出租车位',
};

/**
 * 业主审核状态
 */
export const ownerCheckStatusMap: Record<string, string> = {
  0: '审核通过',
  1: '审核未通过',
};

/**
 * 修改业主详情参数类型定义
 */
export interface ModifyOwnerDetailParams {
  /**
   * 用户头像URL
   */
  avatar: string;

  /**
   * 电子邮箱
   */
  email: string;

  /**
   * 身份证号码
   */
  idNumber: string;

  /**
   * 用户昵称
   */
  nickName: string;

  /**
   * 旧密码
   */
  oldPassword?: string;

  /**
   * 第三方开放ID
   */
  openId: string;

  /**
   * 新密码
   */
  password?: string;

  /**
   * 手机号码
   */
  phonenumber: string;

  /**
   * 房间编号
   */
  roomCode?: string;

  /**
   * 房间ID
   */
  roomId?: number;

  /**
   * 房间名称
   */
  roomName?: string;

  /**
   * 系统唯一序列号
   */
  sequenceNbr: number;

  /**
   * 账号状态
   * @remarks "0"表示正常，"1"表示禁用
   */
  status: string;

  /**
   * 用户类型
   */
  userType: string;
}

export interface AccountBalanceType {
  totalBalance: string;
  accounts: Array<AccountListType>;
}

/**
 * 账户列表类型定义
 */
export interface AccountListType {
  balance: string;
  accountName: string;
  accountType: string;
}

export enum accountCategoryEnum {
  BASIC = 'BASIC',
  SPECIAL = 'SPECIAL',
}

/**
 * 账户类别枚举
 */
export const accountCategoryMap: Record<accountCategoryEnum, string> = {
  [accountCategoryEnum.BASIC]: '基础账户',
  [accountCategoryEnum.SPECIAL]: '专项账户',
};

/**
 * 账户详情类型定义
 */
export interface AccountDetailType {
  /**
   * 系统唯一序列号
   */
  sequenceNbr: string;

  /**
   * 记录创建用户ID
   */
  recUserId: string;

  /**
   * 记录创建日期
   */
  recDate: string;

  /**
   * 账户类别
   * @remarks SPECIAL表示特殊账户
   */
  accountCategory: accountCategoryEnum;

  /**
   * 账户编号
   */
  accountNo: string;

  /**
   * 账户余额
   */
  balance: string;

  /**
   * 账户类型编码
   * @example "JU_MIN_YONG_SHUI_1_._5"
   */
  accountType: string;

  /**
   * 账户状态
   * @remarks NORMAL表示正常状态
   */
  accountStatus: string;

  /**
   * 支付密码
   */
  paymentPassword: string;

  /**
   * 累计余额
   */
  cumulativeBalance: string;

  /**
   * 更新版本号
   */
  updateVersion: string;

  /**
   * 创建时间
   */
  createTime: string;

  /**
   * 用户ID
   */
  userId: string;

  /**
   * 冻结余额
   */
  frozenBalance: string;

  /**
   * 已消费金额
   */
  spendAmount: string;

  /**
   * 冻结更新版本号
   */
  frozenUpdateVersion: string;

  /**
   * 账户名称
   * @example "居民用水1.5"
   */
  accountName: string;
}

/**
 * 账户流水详情参数类型定义
 */
export interface AccountInformationDetailType {
  /**
   * 系统唯一序列号
   */
  sequenceNbr: string;

  /**
   * 记录创建用户ID
   */
  recUserId: string;

  /**
   * 记录创建日期
   */
  recDate: string;

  /**
   * 账户编号
   */
  accountNo: string;

  /**
   * 初始预存余额
   */
  initialPrefundedBalance: string;

  /**
   * 变更金额
   */
  changeAmount: string;

  /**
   * 最终余额
   */
  finalBalance: string;

  /**
   * 操作类型
   * @remarks TRANSFER_INTERNAL表示内部转账
   */
  operationType: string;

  /**
   * 操作时间
   */
  operateTime: string;

  /**
   * 单据编号
   */
  billNo: string;

  /**
   * 更新版本号
   */
  updateVersion: string;

  /**
   * 账户类型编码
   * @example "JU_MIN_YONG_SHUI_1_._5"
   */
  accountType: string;

  /**
   * 记账方向
   * @remarks INCOME表示收入
   */
  bookkeeping: string;

  /**
   * 用户ID
   */
  userId: string;

  /**
   * 账户类别
   * @remarks SPECIAL表示特殊账户
   */
  accountCategory: string;

  /**
   * 附言
   * @example "手动转账"
   */
  postscript: string;
}
